import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchMatchStatistics } from '@/api';
import { Badge } from '@/components/ui/badge';
import { Activity, RefreshCw, Clock, Target, Zap, BarChart3, Shield, AlertTriangle, ChevronDown, ChevronUp, TrendingUp, Users, Award } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';

export default function MatchStatisticsSection({ matchSlug, matchStatus }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const isLive = matchStatus === 'live';

  const load = useCallback(() => {
    fetchMatchStatistics(matchSlug)
      .then(res => { setData(res.data); setLastUpdate(new Date()); setLoading(false); setError(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [matchSlug]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isLive) return;
    const i = setInterval(load, 20000);
    return () => clearInterval(i);
  }, [isLive, load]);

  if (loading) return <StatisticsSkeleton />;
  if (error) return <StatisticsErrorState onRetry={load} />;

  const hasStats = data?.has_data;
  const hasPredictions = data?.has_predictions;
  const hasEvents = data?.has_events;
  const hasLineups = data?.has_lineups;
  const hasAnyContent = hasStats || hasPredictions || hasEvents || hasLineups;

  if (!hasAnyContent) return <StatisticsEmptyState matchStatus={matchStatus} />;

  return (
    <div data-testid="match-statistics-section" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isLive && <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs animate-pulse">LIVE</Badge>}
          {hasStats && <span className="text-xs text-slate-500">{data.stats_count} statistics</span>}
        </div>
        {lastUpdate && (
          <span className="text-[10px] text-slate-600 flex items-center gap-1">
            <Clock className="w-3 h-3" /> {lastUpdate.toLocaleTimeString()}
            {isLive && <RefreshCw className="w-3 h-3 ml-1 animate-spin text-slate-500" />}
          </span>
        )}
      </div>

      {/* Predictions Section (works for ALL matches including prematch) */}
      {hasPredictions && <PredictionsSection predictions={data.predictions} homeTeam={data.home_team} awayTeam={data.away_team} homeLogo={data.home_team_logo} awayLogo={data.away_team_logo} />}

      {/* Match Statistics (only for played/live) */}
      {hasStats && (
        <>
          <SummaryCards groups={data.groups} />
          <div className="space-y-3" data-testid="statistics-groups">
            {data.groups.map(g => <StatisticsGroupCard key={g.group_key} group={g} />)}
          </div>
        </>
      )}

      {/* Timeline Charts */}
      {hasStats && <TimelineCharts groups={data.groups} homeTeam={data.home_team} awayTeam={data.away_team} />}

      {/* Events Timeline */}
      {hasEvents && <EventsSection events={data.events} />}

      {/* Lineups */}
      {hasLineups && <LineupsSection lineups={data.lineups} />}

      {/* H2H */}
      {hasPredictions && data.predictions.h2h?.length > 0 && <H2HSection h2h={data.predictions.h2h} />}
    </div>
  );
}

// ── TIMELINE CHARTS ──
function TimelineCharts({ groups, homeTeam, awayTeam }) {
  // Build chart data from key stats across groups
  const chartStats = useMemo(() => {
    const keyStats = ['shots_total', 'shots_on_target', 'possession', 'corners', 'fouls', 'yellow_cards', 'xg', 'passes_total', 'gk_saves', 'offsides'];
    const found = [];
    for (const g of groups) {
      for (const s of g.stats) {
        if (keyStats.includes(s.key) && s.home_value != null && s.away_value != null) {
          found.push(s);
        }
      }
    }
    return found;
  }, [groups]);

  if (chartStats.length < 3) return null;

  const chartData = chartStats.map(s => ({
    name: s.short_label || s.label,
    home: typeof s.home_value === 'number' ? s.home_value : parseFloat(s.home_value) || 0,
    away: typeof s.away_value === 'number' ? s.away_value : parseFloat(s.away_value) || 0,
  }));

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5" data-testid="timeline-charts">
      <h3 className="text-sm font-semibold text-slate-200 mb-4 flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-cyan-400" /> Stats Comparison Chart
      </h3>
      <div className="flex items-center gap-4 text-xs text-slate-500 mb-3">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-cyan-500/70 inline-block" /> {homeTeam}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-500/70 inline-block" /> {awayTeam}</span>
      </div>
      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical" margin={{ left: 0, right: 10, top: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
            <XAxis type="number" stroke="#475569" tick={{ fill: '#64748b', fontSize: 10 }} />
            <YAxis type="category" dataKey="name" width={80} stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 10 }} />
            <Tooltip
              contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, fontSize: 11 }}
              formatter={(v, name) => [typeof v === 'number' && v % 1 !== 0 ? v.toFixed(2) : v, name === 'home' ? homeTeam : awayTeam]}
            />
            <Bar dataKey="home" fill="#06b6d4" opacity={0.7} radius={[0, 4, 4, 0]} barSize={10} />
            <Bar dataKey="away" fill="#f43f5e" opacity={0.7} radius={[0, 4, 4, 0]} barSize={10} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}


// ── PREDICTIONS ──
function PredictionsSection({ predictions, homeTeam, awayTeam, homeLogo, awayLogo }) {
  const { winner, advice, percent, comparison, team_form } = predictions;
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5" data-testid="predictions-section">
      <h3 className="text-sm font-semibold text-slate-200 mb-4 flex items-center gap-2">
        <Award className="w-4 h-4 text-amber-400" /> Match Prediction
      </h3>
      {/* Win Probability Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-xs text-slate-400 mb-1.5">
          <span className="flex items-center gap-1.5">{homeLogo && <img src={homeLogo} alt="" className="w-4 h-4"/>}{homeTeam}</span>
          <span>Draw</span>
          <span className="flex items-center gap-1.5">{awayTeam}{awayLogo && <img src={awayLogo} alt="" className="w-4 h-4"/>}</span>
        </div>
        <div className="flex h-3 rounded-full overflow-hidden bg-slate-800 gap-px">
          <div className="bg-cyan-500/80 rounded-l-full transition-all" style={{width: percent?.home || '33%'}} />
          <div className="bg-slate-500/60 transition-all" style={{width: percent?.draw || '34%'}} />
          <div className="bg-rose-500/80 rounded-r-full transition-all" style={{width: percent?.away || '33%'}} />
        </div>
        <div className="flex justify-between text-xs font-bold mt-1">
          <span className="text-cyan-400">{percent?.home}</span>
          <span className="text-slate-500">{percent?.draw}</span>
          <span className="text-rose-400">{percent?.away}</span>
        </div>
      </div>
      {/* Advice */}
      {advice && <div className="text-center mb-4"><Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-xs">{advice}</Badge></div>}
      {/* Comparison Bars */}
      {comparison?.length > 0 && (
        <div className="space-y-2">
          {comparison.filter(c => c.key !== 'total').map(c => (
            <div key={c.key} className="flex items-center gap-2 text-xs">
              <span className="w-12 text-right text-cyan-400 font-medium tabular-nums">{c.home_value}</span>
              <div className="flex-1">
                <CompareBarPct home={c.home_value} away={c.away_value} />
                <p className="text-center text-[10px] text-slate-500 mt-0.5">{c.label}</p>
              </div>
              <span className="w-12 text-left text-rose-400 font-medium tabular-nums">{c.away_value}</span>
            </div>
          ))}
        </div>
      )}
      {/* Team Form */}
      {team_form && (
        <div className="grid grid-cols-2 gap-3 mt-4">
          {['home', 'away'].map(side => {
            const tf = team_form[side];
            if (!tf) return null;
            const form = tf.form || tf.season_form || '';
            return (
              <div key={side} className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-xs text-slate-400 mb-2 font-medium">{tf.name || (side === 'home' ? 'Home' : 'Away')} - Last 5</p>
                {form && <div className="flex gap-0.5 mb-2">{form.split('').map((ch, i) => (
                  <span key={i} className={`w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center ${
                    ch === 'W' ? 'bg-emerald-500/20 text-emerald-400' : ch === 'L' ? 'bg-red-500/20 text-red-400' : 'bg-slate-700 text-slate-400'
                  }`}>{ch}</span>
                ))}</div>}
                <div className="text-[10px] text-slate-500 space-y-0.5">
                  <div className="flex justify-between"><span>Goals For</span><span className="text-slate-300">{tf.goals_for}</span></div>
                  <div className="flex justify-between"><span>Goals Against</span><span className="text-slate-300">{tf.goals_against}</span></div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── EVENTS ──
function EventsSection({ events }) {
  if (!events?.length) return null;
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5" data-testid="events-section">
      <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
        <Activity className="w-4 h-4 text-cyan-400" /> Match Events
      </h3>
      <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
        {events.map((ev, i) => {
          const icon = ev.type === 'Goal' ? '⚽' : ev.type === 'Card' ? (ev.detail?.includes('Yellow') ? '🟡' : '🔴') : ev.type === 'subst' ? '🔄' : '•';
          return (
            <div key={i} className="flex items-center gap-2 text-xs py-1 px-2 rounded hover:bg-slate-800/50">
              <span className="w-8 text-right text-slate-500 font-mono tabular-nums shrink-0">
                {ev.minute}'{ev.extra_minute ? `+${ev.extra_minute}` : ''}
              </span>
              <span className="w-5 text-center">{icon}</span>
              <span className="text-slate-300 flex-1 truncate">
                <span className="font-medium">{ev.player}</span>
                {ev.assist && <span className="text-slate-500"> (assist: {ev.assist})</span>}
              </span>
              <span className="text-slate-500 text-[10px] shrink-0">{ev.team}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── LINEUPS ──
function LineupsSection({ lineups }) {
  if (!lineups?.length) return null;
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5" data-testid="lineups-section">
      <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
        <Users className="w-4 h-4 text-purple-400" /> Lineups
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {lineups.map((t, ti) => (
          <div key={ti}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-300">{t.team}</span>
              <Badge variant="outline" className="border-slate-700 text-slate-400 text-[10px]">{t.formation}</Badge>
            </div>
            {t.coach && <p className="text-[10px] text-slate-500 mb-2">Coach: {t.coach}</p>}
            <div className="space-y-0.5">
              {t.starting_xi.map((p, pi) => (
                <div key={pi} className="flex items-center gap-2 text-xs py-0.5">
                  <span className="w-5 text-right text-slate-600 tabular-nums">{p.number}</span>
                  <span className="text-slate-300">{p.name}</span>
                  {p.pos && <span className="text-[10px] text-slate-600 ml-auto">{p.pos}</span>}
                </div>
              ))}
            </div>
            {t.substitutes?.length > 0 && (
              <details className="mt-2">
                <summary className="text-[10px] text-slate-500 cursor-pointer hover:text-slate-400">
                  Substitutes ({t.substitutes.length})
                </summary>
                <div className="space-y-0.5 mt-1">
                  {t.substitutes.map((p, pi) => (
                    <div key={pi} className="flex items-center gap-2 text-xs py-0.5 text-slate-500">
                      <span className="w-5 text-right tabular-nums">{p.number}</span>
                      <span>{p.name}</span>
                      {p.pos && <span className="text-[10px] ml-auto">{p.pos}</span>}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── H2H ──
function H2HSection({ h2h }) {
  if (!h2h?.length) return null;
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5" data-testid="h2h-section">
      <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-blue-400" /> Head to Head ({h2h.length} matches)
      </h3>
      <div className="space-y-1.5">
        {h2h.map((m, i) => (
          <div key={i} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded hover:bg-slate-800/50">
            <span className="text-slate-600 w-20 shrink-0">{m.date}</span>
            <span className={`flex-1 text-right truncate ${m.home_winner ? 'text-emerald-400 font-medium' : 'text-slate-400'}`}>{m.home_team}</span>
            <span className="font-bold text-slate-200 tabular-nums w-10 text-center">{m.home_goals} - {m.away_goals}</span>
            <span className={`flex-1 truncate ${m.away_winner ? 'text-emerald-400 font-medium' : 'text-slate-400'}`}>{m.away_team}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── SUMMARY CARDS ──
function SummaryCards({ groups }) {
  const highlights = useMemo(() => {
    const items = [];
    for (const g of groups) for (const s of g.stats) if (s.is_highlighted && s.home_value != null && s.away_value != null) items.push(s);
    return items.slice(0, 4);
  }, [groups]);
  if (!highlights.length) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="summary-cards">
      {highlights.map(stat => {
        const hw = typeof stat.home_value === 'number' && typeof stat.away_value === 'number' && stat.home_value > stat.away_value;
        const aw = typeof stat.home_value === 'number' && typeof stat.away_value === 'number' && stat.away_value > stat.home_value;
        return (
          <div key={stat.key} className="bg-slate-900/50 border border-slate-800 rounded-lg p-3">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">{stat.short_label}</p>
            <div className="flex items-center justify-between">
              <span className={`text-lg font-bold tabular-nums ${hw ? 'text-emerald-400' : 'text-slate-300'}`}>{fmtVal(stat.home_value, stat.type, stat.unit)}</span>
              <span className="text-[10px] text-slate-600">vs</span>
              <span className={`text-lg font-bold tabular-nums ${aw ? 'text-emerald-400' : 'text-slate-300'}`}>{fmtVal(stat.away_value, stat.type, stat.unit)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── GROUP CARD ──
function StatisticsGroupCard({ group }) {
  const [exp, setExp] = useState(true);
  const ICONS = { shooting: Target, possession_passing: BarChart3, discipline: AlertTriangle, defence: Shield, attacking_pressure: Zap, advanced: Activity };
  const Icon = ICONS[group.group_key] || BarChart3;
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden" data-testid={`stat-group-${group.group_key}`}>
      <button onClick={() => setExp(!exp)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-800/30 transition-colors" aria-expanded={exp}>
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-slate-500" /><h4 className="text-sm font-semibold text-slate-200">{group.group_label}</h4>
          <Badge variant="outline" className="border-slate-700 text-slate-500 text-[10px]">{group.stats.length}</Badge>
        </div>
        {exp ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
      </button>
      {exp && <div className="px-4 pb-3 space-y-1">{group.stats.map(s => <StatRow key={s.key} stat={s} />)}</div>}
    </div>
  );
}

// ── STAT ROW ──
function StatRow({ stat }) {
  const h = typeof stat.home_value === 'number' ? stat.home_value : 0;
  const a = typeof stat.away_value === 'number' ? stat.away_value : 0;
  return (
    <div className="py-1.5" data-testid={`stat-row-${stat.key}`}>
      <div className="hidden sm:flex items-center gap-2">
        <span className={`text-sm font-semibold tabular-nums w-14 text-right shrink-0 ${h > a ? 'text-emerald-400' : 'text-slate-300'}`}>{fmtVal(stat.home_value, stat.type, stat.unit)}</span>
        <div className="flex-1 min-w-0">
          <CompareBar homeValue={h} awayValue={a} type={stat.type} />
          <p className="text-center text-[11px] text-slate-400 mt-0.5 truncate">{stat.label}</p>
        </div>
        <span className={`text-sm font-semibold tabular-nums w-14 shrink-0 ${a > h ? 'text-emerald-400' : 'text-slate-300'}`}>{fmtVal(stat.away_value, stat.type, stat.unit)}</span>
      </div>
      <div className="sm:hidden">
        <p className="text-[11px] text-slate-400 mb-1">{stat.label}</p>
        <div className="flex items-center justify-between mb-1">
          <span className={`text-sm font-semibold tabular-nums ${h > a ? 'text-emerald-400' : 'text-slate-300'}`}>{fmtVal(stat.home_value, stat.type, stat.unit)}</span>
          <span className={`text-sm font-semibold tabular-nums ${a > h ? 'text-emerald-400' : 'text-slate-300'}`}>{fmtVal(stat.away_value, stat.type, stat.unit)}</span>
        </div>
        <CompareBar homeValue={h} awayValue={a} type={stat.type} />
      </div>
    </div>
  );
}

// ── COMPARE BARS ──
function CompareBar({ homeValue, awayValue, type }) {
  let hp, ap;
  if (type === 'percentage') { hp = homeValue; ap = awayValue; }
  else { const t = homeValue + awayValue; if (t === 0) { hp = 50; ap = 50; } else { hp = (homeValue / t) * 100; ap = (awayValue / t) * 100; } }
  return (
    <div className="flex h-1.5 rounded-full overflow-hidden bg-slate-800/50 gap-px">
      <div className="rounded-l-full transition-all duration-500 bg-cyan-500/70" style={{ width: `${Math.max(hp, 2)}%` }} />
      <div className="rounded-r-full transition-all duration-500 bg-rose-500/70" style={{ width: `${Math.max(ap, 2)}%` }} />
    </div>
  );
}

function CompareBarPct({ home, away }) {
  const hp = parseFloat(home) || 50;
  const ap = parseFloat(away) || 50;
  return (
    <div className="flex h-1.5 rounded-full overflow-hidden bg-slate-800/50 gap-px">
      <div className="rounded-l-full bg-cyan-500/70" style={{ width: `${Math.max(hp, 2)}%` }} />
      <div className="rounded-r-full bg-rose-500/70" style={{ width: `${Math.max(ap, 2)}%` }} />
    </div>
  );
}

// ── STATES ──
function StatisticsSkeleton() {
  return (
    <div className="space-y-4 animate-pulse" data-testid="statistics-skeleton">
      <div className="h-40 bg-slate-800/30 rounded-xl" />
      <div className="grid grid-cols-4 gap-2">{[1,2,3,4].map(i => <div key={i} className="h-16 bg-slate-800/50 rounded-lg" />)}</div>
      {[1,2].map(i => <div key={i} className="h-32 bg-slate-800/30 rounded-xl" />)}
    </div>
  );
}

function StatisticsEmptyState({ matchStatus }) {
  return (
    <div className="text-center py-12 bg-slate-900/50 border border-slate-800 rounded-xl" data-testid="statistics-empty">
      {matchStatus === 'prematch' ? (
        <><Clock className="w-10 h-10 text-amber-400 mx-auto mb-3 opacity-60" />
        <h3 className="text-sm font-medium text-slate-200 mb-1">Match Not Started Yet</h3>
        <p className="text-xs text-slate-500 max-w-sm mx-auto">Live statistics will appear once the match kicks off.</p></>
      ) : (
        <><BarChart3 className="w-10 h-10 text-slate-600 mx-auto mb-3" />
        <h3 className="text-sm font-medium text-slate-300 mb-1">No Statistics Available</h3>
        <p className="text-xs text-slate-500 max-w-sm mx-auto">Statistics are not available for this fixture.</p></>
      )}
    </div>
  );
}

function StatisticsErrorState({ onRetry }) {
  return (
    <div className="text-center py-12 bg-slate-900/50 border border-red-500/20 rounded-xl" data-testid="statistics-error">
      <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3 opacity-60" />
      <h3 className="text-sm font-medium text-slate-200 mb-1">Unable to Load Statistics</h3>
      <p className="text-xs text-slate-500 mb-3">Something went wrong.</p>
      <button onClick={onRetry} className="text-xs text-cyan-400 hover:text-cyan-300 border border-cyan-500/30 rounded-lg px-4 py-1.5" data-testid="retry-button">
        <RefreshCw className="w-3 h-3 inline mr-1" /> Try Again
      </button>
    </div>
  );
}

function fmtVal(v, type, unit) {
  if (v == null) return '—';
  if (type === 'percentage') return `${v}%`;
  if (type === 'decimal') return Number(v).toFixed(2);
  if (unit) return `${v}${unit}`;
  return String(v);
}
